import apiClient from '@/shared/services/client'
import type {
  ConfiguracionCEMC,
  FuenteDatosCEMC,
  CursoCEMC,
  EstadisticasCEMC,
  PaginatedConfiguracionesCEMC,
  ConfigNavItem,
} from '../types'

const BASE = '/cemc-educacion'

export const cemcApi = {
  // Configuraciones
  getConfiguraciones: async (params?: { page?: number; search?: string }): Promise<PaginatedConfiguracionesCEMC> => {
    const { data } = await apiClient.get(`${BASE}/configuraciones/`, { params })
    return data
  },
  // Configuraciones accesibles por los grupos del usuario (navegación del sidebar)
  getMisConfiguraciones: async (): Promise<ConfigNavItem[]> => {
    const { data } = await apiClient.get(`${BASE}/mis-configuraciones/`)
    return data
  },
  getConfiguracion: async (id: number): Promise<ConfiguracionCEMC> => {
    const { data } = await apiClient.get(`${BASE}/configuracion/${id}/`)
    return data
  },
  createConfiguracion: async (payload: Partial<ConfiguracionCEMC>): Promise<ConfiguracionCEMC> => {
    const { data } = await apiClient.post(`${BASE}/configuracion/`, payload)
    return data
  },
  updateConfiguracion: async (id: number, payload: Partial<ConfiguracionCEMC>): Promise<ConfiguracionCEMC> => {
    const { data } = await apiClient.put(`${BASE}/configuracion/${id}/`, payload)
    return data
  },
  deleteConfiguracion: async (id: number): Promise<void> => {
    await apiClient.delete(`${BASE}/configuracion/${id}/`)
  },
  toggleActivo: async (id: number): Promise<{ message: string }> => {
    const { data } = await apiClient.post(`${BASE}/configuracion/${id}/toggle-activo/`)
    return data
  },

  // Fuentes
  getFuentes: async (configId: number): Promise<FuenteDatosCEMC[]> => {
    const { data } = await apiClient.get(`${BASE}/configuracion/${configId}/fuentes/`)
    return data
  },
  createFuente: async (configId: number, payload: Partial<FuenteDatosCEMC>): Promise<FuenteDatosCEMC> => {
    const { data } = await apiClient.post(`${BASE}/configuracion/${configId}/fuentes/`, payload)
    return data
  },
  updateFuente: async (id: number, payload: Partial<FuenteDatosCEMC>): Promise<FuenteDatosCEMC> => {
    const { data } = await apiClient.put(`${BASE}/fuente/${id}/`, payload)
    return data
  },
  deleteFuente: async (id: number): Promise<void> => {
    await apiClient.delete(`${BASE}/fuente/${id}/`)
  },
  testConexion: async (payload: Record<string, string>): Promise<{ success: boolean; message: string; columnas?: string[] }> => {
    const { data } = await apiClient.post(`${BASE}/fuente/test-conexion/`, payload)
    return data
  },
  testGoogleSheets: async (payload: { google_sheet_url: string; google_credentials_json?: string }): Promise<{ success: boolean; message: string; columnas?: string[]; tiempo_ms?: number }> => {
    const { data } = await apiClient.post(`${BASE}/fuente/test-google-sheets/`, payload)
    return data
  },
  uploadArchivo: async (fuenteId: number, file: File): Promise<FuenteDatosCEMC> => {
    const fd = new FormData()
    fd.append('file', file)
    const { data } = await apiClient.post(
      `${BASE}/fuente/${fuenteId}/upload-archivo/`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return data
  },

  // Cursos
  getCursos: async (configId: number): Promise<CursoCEMC[]> => {
    const { data } = await apiClient.get(`${BASE}/configuracion/${configId}/cursos/`)
    return data
  },
  createCurso: async (configId: number, payload: Partial<CursoCEMC>): Promise<CursoCEMC> => {
    const { data } = await apiClient.post(`${BASE}/configuracion/${configId}/cursos/`, payload)
    return data
  },
  updateCurso: async (id: number, payload: Partial<CursoCEMC>): Promise<CursoCEMC> => {
    const { data } = await apiClient.put(`${BASE}/curso/${id}/`, payload)
    return data
  },
  deleteCurso: async (id: number): Promise<void> => {
    await apiClient.delete(`${BASE}/curso/${id}/`)
  },

  // Usuarios por módulo
  getUsuariosModulo: async (configId: number, cursoId: number, moduloNum: number, params?: { page?: number; page_size?: number; search?: string; sort_field?: string; sort_dir?: string }): Promise<import('../types').PaginatedUsuariosModulo> => {
    const { data } = await apiClient.get(`/cemc-educacion/reporte/${configId}/usuarios/${cursoId}/modulo/${moduloNum}/`, { params })
    return data
  },

  // Datos
  getEstadisticas: async (configId: number): Promise<EstadisticasCEMC> => {
    const { data } = await apiClient.get(`${BASE}/reporte/${configId}/estadisticas/`)
    return data
  },
  exportar: async (configId: number, formato = 'excel', cursoCodigo = '') => {
    return apiClient.get(`${BASE}/reporte/${configId}/exportar/`, {
      params: { formato, curso_codigo: cursoCodigo },
      responseType: 'blob',
    })
  },
}
